/**
 * M1 athlete view. Reads the capability token from the URL fragment (#t=…), loads the
 * protocol via get-protocol-by-token, and lets the athlete tap to check off each exercise
 * (post-adherence-by-token). No login, no DB access, no third-party scripts.
 */
import { useEffect, useState } from 'react';
import { getProtocol, markComplete, type Protocol } from './api.js';

function readToken(): string | null {
  const frag = new URLSearchParams(window.location.hash.slice(1));
  return frag.get('t') ?? new URLSearchParams(window.location.search).get('t');
}

export function App() {
  const token = readToken();
  const [protocol, setProtocol] = useState<Protocol | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    getProtocol(token)
      .then(setProtocol)
      .catch((e) => setError(e.message));
  }, [token]);

  async function check(itemId: string) {
    if (!token || done.has(itemId)) return;
    setSaving(itemId);
    try {
      await markComplete(token, itemId);
      setDone((prev) => new Set(prev).add(itemId));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(null);
    }
  }

  if (!token) {
    return (
      <div className="wrap">
        <h1>Nostos</h1>
        <p>No protocol link found. Scan the QR code your trainer gave you.</p>
      </div>
    );
  }

  if (error && !protocol) {
    return (
      <div className="wrap">
        <h1>Nostos</h1>
        <p>{error}</p>
      </div>
    );
  }

  if (!protocol) {
    return (
      <div className="wrap">
        <h1>Nostos</h1>
        <p>Loading your protocol…</p>
      </div>
    );
  }

  const completed = done.size;
  return (
    <div className="wrap">
      <h1>{protocol.title ?? 'Your rehab'}</h1>
      <p className="progress">
        {completed} / {protocol.items.length} done
      </p>
      {error && <p className="err">{error}</p>}
      <ul className="items">
        {protocol.items.map((it) => {
          const isDone = done.has(it.id);
          const prescription = it.holdSeconds
            ? `${it.holdSeconds}s hold`
            : it.sets && it.reps
              ? `${it.sets} × ${it.reps}`
              : '';
          return (
            <li key={it.id} className={isDone ? 'item done' : 'item'}>
              <button className="check" onClick={() => check(it.id)} disabled={isDone || saving === it.id}>
                {isDone ? '✓' : saving === it.id ? '…' : ''}
              </button>
              <div>
                <div className="name">{it.exerciseName}</div>
                {prescription && <div className="meta">{prescription}{it.side ? ` · ${it.side}` : ''}</div>}
                {it.cues && <div className="cues">{it.cues}</div>}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
